import { useState } from 'react';
import type { TaskDto } from '@werft/contracts';
import { dotStyle } from './Labels.js';

/**
 * Projektinsel W03: Titelbild und Akzent einer Aufgabe – in Liste, Board, Tabelle und Fenster dieselbe Identität.
 * Bild immer über die geprüfte Aufgaben-Adresse (nie öffentlich), kleines Vorschaubild, feste Höhe je Darstellung
 * (keine unkontrollierten Bildhöhen), nicht ziehbar. Fehlt das Bild (Quelle im Papierkorb, kein Zugriff), bleibt
 * einfach Platz für den Text – nichts Kaputtes.
 */
export function coverUrl(task: Pick<TaskDto, 'id' | 'cover'>) {
  return task.cover ? `/api/tasks/${task.id}/cover?v=${task.cover.v}` : '';
}

export function TaskCover({ task, className, decorative = false }: { task: Pick<TaskDto, 'id' | 'cover'>; className: string; decorative?: boolean }) {
  const [broken, setBroken] = useState<string | null>(null);
  const url = coverUrl(task);
  if (!task.cover || broken === url) return null;
  return (
    <img
      src={url}
      alt={decorative ? '' : task.cover.alt}
      loading="lazy"
      decoding="async"
      draggable={false}
      onError={() => setBroken(url)}
      data-task-cover=""
      style={{ objectPosition: `50% ${task.cover.y}%` }}
      className={`block object-cover select-none ${className}`}
    />
  );
}

/** Akzent als schmaler Streifen – färbt nie Titel, Warnungen oder Fokus */
export function AccentBar({ accent, className }: { accent: TaskDto['accent']; className: string }) {
  if (!accent) return null;
  const d = dotStyle(accent);
  return <span aria-hidden data-accent={accent} className={`pointer-events-none absolute ${d.className} ${className}`} style={d.style} />;
}

/**
 * Vorschaubild im Browser erzeugen (Original bleibt in der Ablage): höchstens 640 px breit, WebP – so lädt ein Board
 * mit vielen Karten nie die Originale.
 */
export async function makeThumb(source: Blob, maxWidth = 640): Promise<{ blob: Blob; ratio: number }> {
  const bitmap = await createImageBitmap(source);
  const ratio = bitmap.width / bitmap.height;
  const scale = Math.min(1, maxWidth / bitmap.width);
  const w = Math.max(1, Math.round(bitmap.width * scale));
  const h = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  canvas.getContext('2d')!.drawImage(bitmap, 0, 0, w, h);
  bitmap.close?.();
  const blob = await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Vorschaubild konnte nicht erzeugt werden.'))), 'image/webp', 0.82),
  );
  // Ältere Browser liefern statt WebP ein PNG – beides nimmt der Server an
  return { blob, ratio: ratio || w / h };
}
